/**
 * Die Grund-Pille in „Du bist dran“ (Antoine, 20.10.2026, Mockup V5 „Grund-Pille Du bist dran“
 * CEJNDTdmQcnh4S5SEqUJAX, freigegeben 00:40): GENAU EINE Pille je Karte sagt auf den ersten Blick,
 * WARUM der Vorgang beim Betreiber liegt. Anlass V-1197 (Ava Karatas): Eingangsbestätigung
 * „550 mailbox unavailable“, drei Neuversände „Brevo blocked“ — die Karte sagte „Angebot gesendet“.
 *
 * Rein anzeigend und rein lesend: die Gründe kommen aus der bestehenden Spaltenlogik
 * (`stand.regel`, `@maestro/anfrage-stand`) und den Tatsachen des Servers — nichts wird geraten und
 * nichts gerechnet außer der Rangfolge. Beträge kommen hier nirgends vor.
 *
 * Rangfolge (der dringendere Grund gewinnt):
 *   Mail nicht zugestellt › Nachricht vom Gast › Neue Anfrage › Angebot nicht gesendet /
 *   Angebot ohne Positionen › N× geöffnet, keine Antwort › Angebot wird erstellt.
 * Nur für die Gruppe `du_bist_dran`.
 *
 * Farben: Erfolg grün, Fehler rot, Orange nur für Knöpfe (Antoine 07.10.2026, „Ruhige Hinweise“).
 * Die Etikett-Farben sind VOLLTON (deckend, kein Alpha): das Etikett liegt auf der Kartenkante und
 * der Rand darunter darf nicht durchscheinen. Alle Hex-Werte stehen an dieser einen Stelle (die
 * Ratsche `test/ruhige-hinweise.test.ts` nimmt genau diese Datei aus).
 * Ausnahme dort eingetragen: Antoine 20.10.2026, Mockup CEJNDTdmQcnh4S5SEqUJAX.
 */
import type { AnfrageStand } from "@maestro/anfrage-stand";
import type { AnfrageTatsachen } from "../ui/types";

export type GrundPilleTon = "error" | "success" | "accent" | "warn" | "info";
export type GrundPilleArt = "zustellung" | "gast_antwort" | "neue_anfrage" | "unversendet" | "leer" | "heiss" | "generiert";

export interface GrundPille {
  art: GrundPilleArt;
  label: string;
  tone: GrundPilleTon;
  /** Material-Symbol. */
  icon: string;
  /** Tooltip; bei „Mail nicht zugestellt“ der Grund in Worten. */
  title?: string;
}

export interface GrundPilleEingabe {
  stand?: AnfrageStand | null;
  tatsachen?: AnfrageTatsachen | null;
}

/** Die Pille einer Karte, oder `null` (andere Gruppe oder kein Grund, der eine Pille verdient). */
export function grundPille({ stand, tatsachen }: GrundPilleEingabe): GrundPille | null {
  if (!stand || stand.gruppe !== "du_bist_dran") return null;

  // 1. Mail nicht zugestellt — die Regel, oder die Tatsache allein (die Eingangsbestätigung eines
  //    Vorgangs ohne Angebot kommt zurück, bevor die Regel greifen kann).
  if (stand.regel === "zustellung_fehlgeschlagen" || tatsachen?.zustellungFehlgeschlagen === true) {
    return {
      art: "zustellung",
      label: "Mail nicht zugestellt",
      tone: "error",
      icon: "mail_off",
      title: tatsachen?.zustellung?.grundText ?? "Nicht zugestellt",
    };
  }

  // 2. Nachricht vom Gast — ungelesene Antwort (Paket 1, jetzt Teil dieser einen Pille).
  if (tatsachen?.ungeleseneGastAntwortAm) {
    return { art: "gast_antwort", label: "Nachricht vom Gast", tone: "success", icon: "mark_email_unread", title: "Per E-Mail antworten" };
  }

  switch (stand.regel) {
    case "kein_angebot_gaeste_fehlen":
    case "kein_angebot_rueckfrage":
    case "kein_angebot_vollstaendig":
      return { art: "neue_anfrage", label: "Neue Anfrage", tone: "accent", icon: "fiber_new" };
    case "angebot_unversendet":
      return { art: "unversendet", label: "Angebot nicht gesendet", tone: "warn", icon: "drafts" };
    case "angebot_leer":
      return { art: "leer", label: "Angebot ohne Positionen", tone: "warn", icon: "playlist_add" };
    case "heiss_anrufen": {
      // Die echte Zahl der Aufrufe seit dem letzten Versand (Migration 730); ohne sie die Gesamtzahl.
      const n = tatsachen?.aufrufeSeitVersand ?? tatsachen?.aufrufe ?? null;
      return { art: "heiss", label: n != null && n > 0 ? `${n}× geöffnet, keine Antwort` : "Geöffnet, keine Antwort", tone: "success", icon: "visibility" };
    }
    case "wird_generiert":
      return { art: "generiert", label: "Angebot wird erstellt", tone: "info", icon: "progress_activity" };
    default:
      return null;
  }
}

/**
 * Tailwind-Klassen je Ton: deckender Hintergrund (hell/dunkel) und Textfarbe. Volltonfarben aus dem
 * Mockup — kein Alpha, keine rgba-Tönung.
 */
export const GRUND_PILLE_KLASSEN: Record<GrundPilleTon, string> = {
  accent: "bg-[#F9ECE7] text-[#C2410C] dark:bg-[#412B1C] dark:text-[#EE7A4A]",
  success: "bg-[#E9F2ED] text-[#217A50] dark:bg-[#2F3628] dark:text-[#6FCF97]",
  error: "bg-[#F8E9E8] text-[#B3261E] dark:bg-[#42332B] dark:text-[#F2A59D]",
  warn: "bg-[#F6EFE4] text-[#8A5A12] dark:bg-[#403523] dark:text-[#E0B060]",
  info: "bg-[#EAEDF1] text-[#4B5E78] dark:bg-[#373532] dark:text-[#9FB3CF]",
};

/** Zeile unter dem Namen bei „Mail nicht zugestellt“: „<Adresse> · <Grund>“. */
export function zustellZeile(tatsachen?: AnfrageTatsachen | null): string | null {
  const z = tatsachen?.zustellung;
  if (!z) return null;
  return [z.adresse, z.grundText].filter(Boolean).join(" · ") || null;
}
